import { useMemo, useRef } from 'react';
import { throttle } from 'lodash';
import { useIdentify } from '@hungryroot/customer';
import { usePatchBundleRatingsMutation } from '../../../hooks/usePatchBundleRatingsMutation';
import { usePatchCustomerPairingRatings } from '../../../hooks/usePatchCustomerPairingRatings';
import { usePatchCustomerProductRatings } from '../../../hooks/usePatchCustomerProductRatings';
import {
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';

type UseTextAreaFeedbackProps = {
  /** Thumbs rating from the parent bundle, pairing, or product rating. */
  rating: boolean | null;

  /**
   * The product rating ID. Product rating differ from bundle and pairings since they do not
   * use the product ID. Since there is a chance a product rating has not been created yet,
   * this is optional and API requests will not be made until this is provided.
   */
  productRatingId?: number | null;

  /** The ratable item. */
  item: RatablePairingItem | RatableProductItem;

  /** Error handler for when something goes wrong when attempting to submit the text feedback */
  onError?: () => void;

  /**
   * Whether or not to allow the API requests to be called. This is useful if consumers
   * of this hook want to temporarily halt the API requests (e.g., waiting on some
   * dependencies).
   */
  enabled: boolean;
};

/**
 * Given the props, this hook will handle which endpoint to make the PATCH request to for
 * text feedback.
 */
export function useTextAreaFeedback(props: UseTextAreaFeedbackProps) {
  const {
    rating,
    productRatingId,
    item,
    enabled,
    onError: passThroughOnError,
  } = props;

  // Manage a ref for the last successful text payload so we can compare it to the current
  // text to determine if we need to make an API call
  const lastTextareaValuePayload = useRef<string | null>('');

  const onSuccess = ({ feedback }: { feedback?: string | null }) => {
    lastTextareaValuePayload.current = feedback ?? '';
  };

  const onError = () => {
    passThroughOnError?.();
  };

  const { mutate: patchBundleRatingsMutate } = usePatchBundleRatingsMutation({
    onSuccess,
    onError,
  });

  const { mutate: patchCustomerPairingRatingsMutate } =
    usePatchCustomerPairingRatings({
      onSuccess,
      onError,
    });

  const { mutate: patchCustomerProductRatingsMutate } =
    usePatchCustomerProductRatings({
      onSuccess,
      onError,
    });

  const { customerData } = useIdentify();

  const submitFeedback = useMemo(
    () =>
      throttle(
        (text: string) => {
          const customerId = customerData?.id;

          if (!enabled || customerId === undefined) {
            return;
          }

          if (text !== lastTextareaValuePayload.current) {
            if ('bundleId' in item && item.bundleId !== null) {
              patchBundleRatingsMutate({
                bundle_id: item.bundleId,
                feedback: text,
                customer_id: customerId,
                liked: rating,
                pairing: item.pairingId,
              });
            } else if ('pairingId' in item) {
              patchCustomerPairingRatingsMutate({
                customerId,
                payload: {
                  feedback: text,
                  pairing: item.pairingId,
                  liked: rating,
                  order_id: item.orderNumber,
                },
              });
            } else if ('productId' in item && productRatingId) {
              patchCustomerProductRatingsMutate({
                customerId,
                payload: {
                  customer_id: customerId,
                  feedback: text,
                  id: productRatingId,
                  liked: rating,
                  order: item.orderNumber,
                  product: item.productId,
                },
              });
            }
          }
        },
        2000,
        { leading: false, trailing: true }
      ),
    [
      item,
      productRatingId,
      patchBundleRatingsMutate,
      rating,
      patchCustomerPairingRatingsMutate,
      patchCustomerProductRatingsMutate,
      customerData?.id,
      enabled,
    ]
  );

  return { submitFeedback };
}
